import type {QuoteDocumentData, QuoteEstimationUnit, QuoteLineItem, QuoteRollup, QuoteTemplate} from './resolver/types';

export type EstimateRow =
	| {kind: 'phaseHeader'; index: number; label: string}
	| {kind: 'deliverableHeader'; label: string}
	| {
			kind: 'item';
			itemKind: 'role' | 'expense';
			label: string;
			hours: number | null;
			rate: number | null;
			total: number | null;
	  }
	/** One row per phase carrying that phase's whole expense figure. */
	| {kind: 'expensesTotal'; total: number}
	/** "Subtotal — {phase}": the phase's own hours and amount, on its own tinted row. */
	| {kind: 'phaseSubtotal'; label: string; hours: number; total: number}
	| {kind: 'rollup'; label: string; hours: number | null; total: number};

export type EstimateTotals = {
	/** Sum of role totals — the real amount, regardless of display toggles. */
	subtotal: number;
	/** Sum of expense totals — the real amount, regardless of display toggles. */
	expenses: number;
	/** subtotal + expenses. Display toggles only hide rows, never change the math. */
	grandTotal: number;
};

export type EstimateModel = {
	rows: EstimateRow[];
	totals: EstimateTotals;
	unit: QuoteEstimationUnit;
	minutesPerDay: number;
	showTime: boolean;
	showRate: boolean;
	showAmount: boolean;
	firstColumn: QuoteRollup;
};

const FALLBACK_MINUTES_PER_DAY = 480;

const sum = (values: number[]): number => values.reduce((acc, value) => acc + value, 0);
const roleTotals = (items: QuoteLineItem[]): number => sum(items.filter(i => i.kind === 'role').map(i => i.total));
const expenseTotals = (items: QuoteLineItem[]): number => sum(items.filter(i => i.kind === 'expense').map(i => i.total));
const roleHours = (items: QuoteLineItem[]): number => sum(items.filter(i => i.kind === 'role').map(i => i.hours));

/** Groups items by key preserving first-appearance order. */
const groupInOrder = <K>(items: QuoteLineItem[], keyOf: (item: QuoteLineItem) => K): Map<K, QuoteLineItem[]> => {
	const groups = new Map<K, QuoteLineItem[]>();
	for (const item of items) {
		const key = keyOf(item);
		const group = groups.get(key);
		if (group) {
			group.push(item);
		} else {
			groups.set(key, [item]);
		}
	}
	return groups;
};

/** "12h" / "1.5d" — days use the baseline's minutes-per-day, to at most one decimal. */
export const formatEstimateHours = (hours: number, unit: QuoteEstimationUnit, minutesPerDay: number): string => {
	if (unit !== 'days') {
		return `${Math.round(hours * 100) / 100}h`;
	}
	const hoursPerDay = (minutesPerDay > 0 ? minutesPerDay : FALLBACK_MINUTES_PER_DAY) / 60;
	const days = hours / hoursPerDay;
	const rounded = Math.round(days * 10) / 10;
	return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}d`;
};

/** Per-hour rate expressed in the display unit: unchanged for hours, × hours-per-day for days. */
export const ratePerUnit = (ratePerHour: number, unit: QuoteEstimationUnit, minutesPerDay: number): number => {
	if (unit !== 'days') return ratePerHour;
	return ratePerHour * ((minutesPerDay > 0 ? minutesPerDay : FALLBACK_MINUTES_PER_DAY) / 60);
};

const buildRoleRollupRows = (data: QuoteDocumentData, template: QuoteTemplate): EstimateRow[] => {
	const rows: EstimateRow[] = [];
	const phases = groupInOrder(data.lineItems, item => item.phaseId);
	let phaseIndex = 0;
	phases.forEach(items => {
		phaseIndex += 1;
		rows.push({kind: 'phaseHeader', index: phaseIndex, label: items[0].phaseName});
		const showDeliverableHeaders = data.useDeliverables && template.estimate.grouping.deliverableHeaders;
		const deliverableGroups = groupInOrder(
			items.filter(item => item.kind === 'role'),
			item => item.deliverableId
		);
		deliverableGroups.forEach((groupItems, deliverableId) => {
			if (showDeliverableHeaders && deliverableId != null && groupItems[0].deliverableName) {
				rows.push({kind: 'deliverableHeader', label: groupItems[0].deliverableName});
			}
			for (const item of groupItems) {
				rows.push({
					kind: 'item',
					itemKind: 'role',
					label: item.name,
					hours: item.hours,
					rate: item.rate,
					total: template.estimate.grouping.roleTotals ? item.total : null,
				});
			}
		});
		if (template.estimate.grouping.phaseSubtotals) {
			rows.push({
				kind: 'phaseSubtotal',
				label: items[0].phaseName,
				hours: roleHours(items),
				total: roleTotals(items),
			});
		}
		// One figure for the phase rather than a line per expense: the quote states what the phase
		// costs, and an itemised expense ledger is not what the reader is being asked to approve.
		const expenses = items.filter(item => item.kind === 'expense');
		if (template.estimate.grouping.phaseExpenses && expenses.length > 0) {
			rows.push({kind: 'expensesTotal', total: expenseTotals(expenses)});
		}
	});
	return rows;
};

const buildGroupedRollupRows = (
	data: QuoteDocumentData,
	template: QuoteTemplate,
	keyOf: (item: QuoteLineItem) => string,
	labelOf: (items: QuoteLineItem[]) => string
): EstimateRow[] => {
	const rows: EstimateRow[] = [];
	groupInOrder(data.lineItems, keyOf).forEach(items => {
		rows.push({
			kind: 'rollup',
			label: labelOf(items),
			hours: roleHours(items),
			total: roleTotals(items),
		});
	});
	return rows;
};

/**
 * Computes every estimate row and total from the same frozen line items. Totals always use the
 * real amounts — the display toggles only hide rows.
 */
export const buildEstimate = (data: QuoteDocumentData, template: QuoteTemplate): EstimateModel => {
	const subtotal = roleTotals(data.lineItems);
	const expenses = expenseTotals(data.lineItems);
	const rollup: QuoteRollup =
		template.estimate.rollup === 'deliverable' && !data.useDeliverables ? 'phase' : template.estimate.rollup;

	let rows: EstimateRow[];
	switch (rollup) {
		case 'project':
			rows = [
				{
					kind: 'rollup',
					label: data.projectName,
					hours: roleHours(data.lineItems),
					total: subtotal,
				},
			];
			break;
		case 'phase':
			rows = buildGroupedRollupRows(
				data,
				template,
				item => item.phaseId,
				items => items[0].phaseName
			);
			break;
		case 'deliverable':
			// Items without a deliverable fall back to grouping (and labelling) by their phase.
			rows = buildGroupedRollupRows(
				data,
				template,
				item => (item.deliverableId != null ? `deliverable:${item.deliverableId}` : `phase:${item.phaseId}`),
				items => items[0].deliverableName || items[0].phaseName
			);
			break;
		default:
			rows = buildRoleRollupRows(data, template);
	}

	return {
		rows,
		totals: {
			subtotal,
			expenses,
			grandTotal: subtotal + expenses,
		},
		unit: data.estimationUnit,
		minutesPerDay: data.minutesPerDay > 0 ? data.minutesPerDay : FALLBACK_MINUTES_PER_DAY,
		showTime: template.estimate.columns.time,
		showRate: template.estimate.columns.rate,
		showAmount: template.estimate.columns.amount,
		firstColumn: rollup,
	};
};
